import React, { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

const ProtectedRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  const [isOffline, setIsOffline] = useState(false);
  const [isCheckingNetwork, setIsCheckingNetwork] = useState(false);

  // Check network connectivity when component mounts
  useEffect(() => {
    // Only check network if user appears to be authenticated
    if (!loading && isAuthenticated) {
      checkNetworkAndAuth();
    }
  }, [loading, isAuthenticated]);

  // Function to check network and auth status
  const checkNetworkAndAuth = async () => {
    setIsCheckingNetwork(true);
    
    // Check if we're online
    const isOnline = navigator.onLine;
    
    if (!isOnline) {
      console.log('Network appears to be offline');
      setIsOffline(true);
      setIsCheckingNetwork(false);
      return;
    }
    
    // If we're online, we can proceed with normal flow
    setIsOffline(false);
    setIsCheckingNetwork(false);
  };

  // Show loading spinner while initial auth check is happening
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show checking network message if we're verifying connectivity
  if (isCheckingNetwork) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={30} sx={{ mb: 2 }} />
        <Typography variant="body1">Checking network connectivity...</Typography>
      </Box>
    );
  }

  // If user is authenticated (from localStorage) but we're offline,
  // allow access but show a warning in the layout
  if (isAuthenticated && isOffline) {
    // We'll pass the offline status to the outlet context
    return <Outlet context={{ isOffline: true }} />;
  }

  // Normal flow - authenticated users can access, others are redirected
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
};

export default ProtectedRoute;